import { getSupabaseClient } from '@/infrastructure/supabase/app'

// Private bucket (0037_document_library.sql). Files are reached through short-lived signed URLs,
// never a public link, and the same limits are enforced by the storage policy.
const BUCKET = 'org-documents'

export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024

export const ALLOWED_DOCUMENT_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
]

export interface DocumentFileInfo {
  readonly name: string
  readonly type: string
  readonly size: number
}

export function assertDocumentFileAllowed(file: DocumentFileInfo): void {
  if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
    throw new Error('Formato no permitido. Usa PDF, imagen, documento de Office, CSV o texto.')
  }
  if (file.size <= 0) {
    throw new Error('El archivo está vacío.')
  }
  if (file.size > MAX_DOCUMENT_BYTES) {
    throw new Error(`El archivo supera el máximo de ${Math.round(MAX_DOCUMENT_BYTES / 1024 / 1024)} MB.`)
  }
}

// Drops path separators and collapses dot runs, so a name like '../../x.pdf' cannot walk folders
// nor keep a '..' in the stored object name.
function sanitizeSegment(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/\.{2,}/g, '.')
      .replace(/^[.-]+|[.-]+$/g, '') || 'documento'
  )
}

/** organizations/<organizationId>/documents/<unique>-<name>, the layout app.storage_* expects. */
export function buildDocumentObjectPath(organizationId: string, fileName: string, uniqueId: string): string {
  return ['organizations', sanitizeSegment(organizationId), 'documents', `${uniqueId}-${sanitizeSegment(fileName)}`].join('/')
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** Returns the object path stored in documents.file_url; the URL is signed only when downloading. */
export async function uploadOrganizationDocument(input: {
  readonly organizationId: string
  readonly file: File
}): Promise<string> {
  assertDocumentFileAllowed(input.file)

  const objectPath = buildDocumentObjectPath(input.organizationId, input.file.name, crypto.randomUUID())
  const client = getSupabaseClient()
  const { error } = await client.storage.from(BUCKET).upload(objectPath, input.file, {
    contentType: input.file.type,
    upsert: false,
  })
  if (error) throw error

  return objectPath
}
